/**
 * Tunisian phone number validation (PDF §27): "Format des numéros tunisiens
 * (8 chiffres, indicatif +216) validé à la saisie."
 *
 * The specification states only the shape (8 digits, +216 country code) —
 * no carrier-prefix table is given in the source documents, so none is
 * invented here. Mobile numbers conventionally start with 2, 4, 5 or 9 and
 * landlines with 3 or 7; 0 and 1 are not used as a leading digit for any
 * Tunisian subscriber number, so that much is safe to enforce.
 *
 * The phone number is also the primary customer identifier (DEV §6.4), so
 * this validation runs at every point one is collected: checkout, staff
 * invitation, OTP (once enabled).
 */
const RAW_DIGITS = /[\s.\-()]/g;

export interface PhoneValidationResult {
  readonly valid: boolean;
  /** E.164 form (+216XXXXXXXX), present only when valid. */
  readonly normalized: string | undefined;
  readonly reason: string | undefined;
}

export function validateTunisianPhone(input: string): PhoneValidationResult {
  const stripped = input.trim().replace(RAW_DIGITS, '');

  const withoutCountryCode = stripped.startsWith('+216')
    ? stripped.slice(4)
    : stripped.startsWith('00216')
      ? stripped.slice(5)
      : stripped.startsWith('216') && stripped.length === 11
        ? stripped.slice(3)
        : stripped;

  if (!/^\d{8}$/.test(withoutCountryCode)) {
    return {
      valid: false,
      normalized: undefined,
      reason: 'Le numéro doit contenir exactement 8 chiffres, avec ou sans indicatif +216.',
    };
  }

  if (withoutCountryCode.startsWith('0') || withoutCountryCode.startsWith('1')) {
    return {
      valid: false,
      normalized: undefined,
      reason: 'Un numéro tunisien ne commence jamais par 0 ou 1.',
    };
  }

  return { valid: true, normalized: `+216${withoutCountryCode}`, reason: undefined };
}

export function isValidTunisianPhone(input: string): boolean {
  return validateTunisianPhone(input).valid;
}
